type BENE_CATEGORY = 'ENTITY' | 'PERSON' | 'PRIMARY' | 'SECONDARY' | '';
type MAIN_COMPONENT_PREFIX =
  | 'AddressValidationModal'
  | 'BasicPI'
  | 'Beneficiary'
  | 'BYOP'
  | 'Card'
  | 'Cart'
  | 'ContactInformation'
  | 'CoTrustPI'
  | 'Credentials'
  | 'Custodian'
  | 'CustodianInstitution'
  | 'ExistingFunds'
  | 'FundReview'
  | 'JointOwnerCI'
  | 'JointOwnerPI'
  | 'MinorCI'
  | 'MinorPI'
  | 'PersonalInformation'
  | 'RDF'
  | 'RDFTerms'
  | 'Review'
  | 'RolloverOptions'
  | 'Trust'
  | 'TrusteeContactInformation'
  | 'TrusteePersonalInformation';

/**
 * Capitalizes the first letter of a string and converts the rest of the string to lowercase.
 * @param string - The string to be capitalized.
 * @returns The capitalized string.
 */
export const capitalizeString = (string = ''): string => {
  // lowercase entire string value except for the first character
  const lowerCaseSubstring = string.substring(1).toLowerCase();
  // capitalize the first character
  const firstLetter = string.charAt(0).toUpperCase();
  return `${firstLetter}${lowerCaseSubstring}`;
};

/**
 * Generates a general test ID by combining the main component prefix and an optional element ID.
 * @param mainComponentPrefix The prefix for the main component. See MAIN_COMPNENT_PREFIX type for possible values.
 * @param elementId The identifying string for the form element.
 * @returns The generated test ID; i.e. "PersonalInformation_FirstName"
 */
export const generateGeneralTestID = (
  mainComponentPrefix: MAIN_COMPONENT_PREFIX,
  elementId = ''
): string => `${mainComponentPrefix}_${elementId}`;

/**
 * Generates a test ID for a beneficiary form element.
 * @param elementID - The identifying string for the form element (optional).
 * @param beneCategory - The category of the beneficiary. See BENE_CATEGORY type for possible values.
 * @param beneIndex - The index of the beneficiary within the data array.
 * @returns The generated test ID; i.e. "Beneficiary_BeneficiaryFirstName_Person_0"
 */
export const generateBeneficiaryTestID = (
  elementID = '',
  beneCategory: BENE_CATEGORY = '',
  beneIndex = 0
): string => {
  const baseID = generateGeneralTestID('Beneficiary', elementID);
  // reformat the all caps beneCategory
  const formattedBeneCategory = capitalizeString(beneCategory);
  return `${baseID}_${formattedBeneCategory}_${beneIndex}`;
};

/**
 * Generates a unique test ID for a joint or co-trustee element.
 * @param mainComponentPrefix The prefix for the main component. See MAIN_COMPNENT_PREFIX type for possible values.
 * @param elementId The identifying string for the form element.
 * @param entityIndex The index of the entity.
 * @returns The generated test ID; i.e. "JointOwnerPI_JointOwnerFirstName_0"
 */
export const generateJointOrCoTrustTestID = (
  mainComponentPrefix: MAIN_COMPONENT_PREFIX,
  elementId: '',
  entityIndex = 0
): string => `${mainComponentPrefix}_${elementId}_${entityIndex}`;
